import React from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";

import type { TenantHealth } from "../../models/License";

type UsageItemProps = {
    label: string;
    used: number;
    max: number;
    percent: number;
};

function getUsageState(percent: number): {
    label: string;
    chipColor: "default" | "success" | "warning" | "error";
} {
    if (percent >= 100) return { label: "Limit reached", chipColor: "error" };
    if (percent >= 85) return { label: "Near limit", chipColor: "warning" };
    if (percent >= 70) return { label: "Monitor", chipColor: "default" };
    return { label: "Healthy", chipColor: "success" };
}

const UsageItem: React.FC<UsageItemProps> = ({ label, used, max, percent }) => {
    const state = getUsageState(percent);

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.75}
                gap={2}
            >
                <Box>
                    <Typography variant="body1" fontWeight={500}>
                        {label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {used} / {max}
                    </Typography>
                </Box>

                <Chip
                    label={state.label}
                    color={state.chipColor}
                    size="small"
                    variant={state.chipColor === "default" ? "outlined" : "filled"}
                />
            </Box>

            <LinearProgress
                variant="determinate"
                value={Math.min(percent, 100)}
                sx={{ height: 10, borderRadius: 999 }}
            />

            <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.5}
            >
                {percent}% used
            </Typography>
        </Box>
    );
};

type TenantUsageWidgetProps = {
    health: TenantHealth;
};

const TenantUsageWidget: React.FC<TenantUsageWidgetProps> = ({ health }) => {
    return (
        <Card sx={{ height: "100%" }}>
            <CardContent>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    mb={1}
                >
                    <Box>
                        <Typography variant="h6">Subscription Usage</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {health.planName} · {health.tierName}
                        </Typography>
                    </Box>

                    {health.severity && health.severity !== "ok" && (
                        <Chip
                            label={health.severity.toUpperCase()}
                            color={
                                health.severity === "critical"
                                    ? "error"
                                    : health.severity === "warning"
                                        ? "warning"
                                        : "default"
                            }
                            size="small"
                        />
                    )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2.5}>
                    <UsageItem
                        label="Collections"
                        used={health.clientsUsed}
                        max={health.clientsMax}
                        percent={health?.clientsPercent}
                    />

                    <UsageItem
                        label="Portfolios"
                        used={health.portfoliosUsed}
                        max={health.portfoliosMax}
                        percent={health.portfoliosPercent}
                    />

                    <UsageItem
                        label="Artworks"
                        used={health.artworksUsed}
                        max={health.artworksMax}
                        percent={health.artworksPercent}
                    />
                </Stack>

                <Box mt={2.5}>
                    <Typography variant="caption" color="text.secondary">
                        Usage updates reflect current subscription limits for this tenant.
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export default TenantUsageWidget;